import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, DollarSign, Edit2, Trash2, AlertTriangle, CheckCircle } from "lucide-react";

const BookingManagement = ({ booking, isOpen, onClose, onUpdate, user }) => {
  const [editing, setEditing] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [editData, setEditData] = useState({
    notes: booking?.notes || "",
    paymentMethod: booking?.paymentMethod || "cash"
  });

  const calculateTimeUntil = () => {
    if (!booking) return { hours: 0, canModify: false, canCancel: false };
    
    const bookingDate = new Date(booking.date);
    const [hour, minute] = booking.startTime.split(':');
    bookingDate.setHours(parseInt(hour), parseInt(minute));
    
    const now = new Date();
    const diff = bookingDate - now;
    const hours = diff / (1000 * 60 * 60);
    
    return {
      hours: Math.floor(hours),
      canModify: hours > 12 || booking.isCaptain,
      canCancel: hours > 0
    };
  };

  const getCancellationPenalty = () => {
    const { hours } = calculateTimeUntil();
    if (hours > 12) return { points: 0, message: "Ceza yok" };
    if (hours > 6) return { points: 2, message: "-2 puan" };
    if (hours > 2) return { points: 5, message: "-5 puan" };
    return { points: 10, message: "-10 puan" };
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/bookings/${booking.id}`, editData);
      toast.success("Rezervasyon güncellendi");
      setEditing(false);
      onUpdate();
    } catch (error) {
      const message = error.response?.data?.detail || "Rezervasyon güncellenemedi";
      toast.error(message);
    }
  };

  const handleCancel = async () => {
    try {
      const response = await axios.delete(`${API}/bookings/${booking.id}`);
      const penaltyMessage = response.data.penaltyMessage || "";
      toast.success(`Rezervasyon iptal edildi. ${penaltyMessage}`);
      setShowCancelConfirm(false);
      onUpdate();
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || "Rezervasyon iptal edilemedi";
      toast.error(message);
    }
  };

  if (!booking) return null;

  const timeInfo = calculateTimeUntil();
  const penalty = getCancellationPenalty();
  const isActive = booking.status === 'confirmed' || booking.status === 'pending';

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Rezervasyon Detayları</DialogTitle>
            <DialogDescription>
              {booking.venueName} - Rezervasyon ID: {booking.id.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Status Badge */}
            <div className="flex items-center justify-between">
              <Badge 
                variant={isActive ? "default" : "outline"}
                className={
                  booking.status === 'confirmed' ? 'bg-green-500' :
                  booking.status === 'pending' ? 'bg-yellow-500' :
                  booking.status === 'cancelled' ? 'bg-red-500' :
                  'bg-blue-500'
                }
              >
                {booking.status === 'confirmed' ? 'Onaylandı' :
                 booking.status === 'pending' ? 'Beklemede' :
                 booking.status === 'cancelled' ? 'İptal Edildi' :
                 'Tamamlandı'}
              </Badge>
              
              {timeInfo.hours > 0 && timeInfo.hours < 12 && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700">
                  {timeInfo.hours} saat kaldı
                </Badge>
              )}
            </div>

            {/* Time warning */}
            {timeInfo.hours > 0 && timeInfo.hours < 12 && !booking.isCaptain && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Maça 12 saatten az kaldı. Sadece takım kaptanı değişiklik yapabilir.
                </AlertDescription>
              </Alert>
            )}

            {/* Booking Details */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="h-5 w-5 text-emerald-600" />
                  <div>
                    <div className="text-sm text-gray-500">Saha</div>
                    <div className="font-semibold">{booking.venueName}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="text-sm text-gray-500">Tarih</div>
                    <div className="font-semibold">{new Date(booking.date).toLocaleDateString('tr-TR')}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-gray-700">
                  <Clock className="h-5 w-5 text-purple-600" />
                  <div>
                    <div className="text-sm text-gray-500">Saat</div>
                    <div className="font-semibold">{booking.startTime} - {booking.endTime}</div>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  <div>
                    <div className="text-sm text-gray-500">Ücret</div>
                    <div className="font-semibold text-lg text-emerald-600">{parseFloat(booking.finalPrice).toFixed(2)} TL</div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Ödeme Yöntemi</div>
                  <Badge variant="outline">
                    {booking.paymentMethod === 'cash' ? 'Nakit' :
                     booking.paymentMethod === 'card' ? 'Kart' : 'Online'}
                  </Badge>
                </div>

                <div>
                  <div className="text-sm text-gray-500">Ödeme Durumu</div>
                  <Badge variant={booking.paymentStatus === 'paid' ? 'default' : 'outline'}>
                    {booking.paymentStatus === 'paid' ? 'Ödendi' : 'Beklemede'}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Edit Section */}
            {editing && timeInfo.canModify && isActive && (
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold">Düzenle</h3>
                
                <div>
                  <Label>Ödeme Yöntemi</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={editData.paymentMethod}
                    onChange={(e) => setEditData({...editData, paymentMethod: e.target.value})}
                  >
                    <option value="cash">Nakit</option>
                    <option value="card">Kart</option>
                    <option value="online">Online</option>
                  </select>
                </div>

                <div>
                  <Label>Notlar</Label>
                  <Textarea
                    value={editData.notes}
                    onChange={(e) => setEditData({...editData, notes: e.target.value})}
                    placeholder="Ekstra notlar..."
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={handleUpdate}>Kaydet</Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>İptal</Button>
                </div>
              </div>
            )}

            {booking.notes && !editing && (
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-sm text-gray-600">Notlar:</div>
                <div className="text-gray-800">{booking.notes}</div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {isActive && timeInfo.canModify && !editing && (
              <Button
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Düzenle
              </Button>
            )}
            
            {isActive && timeInfo.canCancel && (
              <Button
                variant="destructive"
                onClick={() => setShowCancelConfirm(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                İptal Et
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rezervasyonu İptal Et</DialogTitle>
            <DialogDescription>
              Bu rezervasyonu iptal etmek istediğinizden emin misiniz?
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {penalty.points > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Ceza Puanı:</strong> Bu rezervasyonu iptal ederseniz <strong>{penalty.message}</strong> alacaksınız.
                  <div className="text-xs mt-1">
                    • 12 saat öncesi: Ceza yok<br/>
                    • 12-6 saat arası: -2 puan<br/>
                    • 6-2 saat arası: -5 puan<br/>
                    • 2 saat ve altı: -10 puan
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {penalty.points === 0 && (
              <Alert>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>
                  Maçtan 12 saatten fazla zaman olduğu için ceza puanı almayacaksınız.
                </AlertDescription>
              </Alert>
            )}

            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-sm text-gray-600">İptal edilecek rezervasyon:</div>
              <div className="font-semibold">{booking.venueName}</div>
              <div className="text-sm">{new Date(booking.date).toLocaleDateString('tr-TR')} - {booking.startTime}</div>
              <div className="text-sm text-emerald-600 font-semibold">{parseFloat(booking.finalPrice).toFixed(2)} TL</div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
              Vazgeç
            </Button>
            <Button variant="destructive" onClick={handleCancel}>
              İptal Et {penalty.points > 0 && `(${penalty.message})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BookingManagement;
